import React, { useEffect, useRef, useState } from 'react';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import FaIcon from '../../../components/FaIcon';

/**
 * Per-channel notification control for the rep reading a client group or a
 * client user's private chat (Nikhil 2026-09-13: "their group + explicits").
 * Reading a channel no longer subscribes a rep to it; a rep is notified for
 *   1. the channels of the clients they own as CS rep (Client.csOwner),
 *   2. the channels they explicitly Follow,
 *   3. @mentions and private-chat hand-offs (unchanged, not shown here).
 * The backend says which applies through
 *     channel.notify = { owner: boolean, following: boolean, muted: boolean,
 *                        mutedUntil: ISO time | null }
 * on every staff-facing channel object (feed rows, the org's /groups list).
 * A missing `notify` (older backend) means unknown → this renders nothing.
 *   owner     → quiet "Notifying you · your client" label, no button
 *   muted     → "Muted", or "Muted until <time>" when notify.mutedUntil says
 *               when a timed mute (8 hours / a week, set in the rep chat app)
 *               ends, with an Unmute button: DELETE
 *               /api/v1/comms/channels/:id/mute answers { notify }, which
 *               replaces the local state. Unmute clears only the mute, so a
 *               follow made before it applies again (the answer says so).
 *               A failed unmute keeps "Muted" and says so in plain words; the
 *               error itself goes to the console only.
 *   otherwise → ☆ Follow / ★ Following, the ticket-follow visual language:
 *               POST / DELETE /api/v1/comms/channels/:id/follow, both answer
 *               { notify } which replaces the local state.
 */

export const OWNER_LABEL = 'Notifying you · your client';
export const OWNER_TITLE = "You get pushes / Chat DMs for this group because you are the client's CS rep";
// The star before them is an icon (FaIcon star, outline = not following; T004), so a label is its words alone.
export const FOLLOW_LABEL = 'Follow';
export const FOLLOWING_LABEL = 'Following';
export const FOLLOW_TITLE = 'Get updates for this group (unread + push)';
export const UNFOLLOW_TITLE = 'Stop getting updates for this group';
export const MUTED_LABEL = 'Muted';
export const MUTED_TITLE = 'You muted this group — no pushes / Chat DMs for it';
export const MUTED_UNTIL_PREFIX = 'Muted until ';
export const UNMUTE_LABEL = 'Unmute';
export const UNMUTE_TITLE = 'Get updates for this group again (a follow you made before the mute applies again)';
export const UNMUTE_FAILED = 'Could not unmute: the group is still muted. Please try again.';

export const follow_path = (channel_id) => `/api/v1/comms/channels/${encodeURIComponent(String(channel_id))}/follow`;
export const mute_path = (channel_id) => `/api/v1/comms/channels/${encodeURIComponent(String(channel_id))}/mute`;

// notify.mutedUntil as the server sent it (an ISO time) when it parses, else null.
const until_of = (v) => (typeof v === 'string' && v !== '' && !Number.isNaN(Date.parse(v)) ? v : null);

/** {owner, following, muted} as booleans plus mutedUntil (the ISO time a timed
 *  mute ends, only while muted; null for an "always" mute or no mute), or null
 *  when the backend sent no notify. */
export const normalise_notify = (notify) => {
    if (!notify || typeof notify !== 'object') return null;
    const muted = !!notify.muted;
    return { owner: !!notify.owner, following: !!notify.following, muted, mutedUntil: muted ? until_of(notify.mutedUntil) : null };
};

/** When a timed mute ends, in the CSP's usual en-IN short form ("28 Oct, 05:30 pm"). */
export const fmt_until = (iso) => {
    try {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
        return '';
    }
};

/** "Muted until <time>" for a timed mute, else "Muted". */
export const muted_label = (notify) => {
    const n = normalise_notify(notify);
    const t = n && n.mutedUntil ? fmt_until(n.mutedUntil) : '';
    return t ? MUTED_UNTIL_PREFIX + t : MUTED_LABEL;
};

/** Which control to show: 'muted' | 'owner' | 'following' | 'follow', or null (unknown → nothing).
 *  Muted wins over everything (it is the truthful state); owner wins over following. */
export const notify_mode = (notify) => {
    const n = normalise_notify(notify);
    if (!n) return null;
    if (n.muted) return 'muted';
    if (n.owner) return 'owner';
    return n.following ? 'following' : 'follow';
};

/** The state after a follow / unfollow call: the returned notify when the
 *  backend sent one, else the previous state with `following` set to what
 *  the (successful) call asked for. */
export const next_notify = (prev, resp, following) => {
    const from_resp = resp && typeof resp === 'object' ? normalise_notify(resp.notify) : null;
    if (from_resp) return from_resp;
    return { ...(normalise_notify(prev) || { owner: false, muted: false, mutedUntil: null }), following: !!following };
};

/** The state after an unmute: the returned notify when the backend sent one
 *  (it decides; a follow made before the mute is back in it), else the
 *  previous state with only the mute cleared, as the route does. */
export const after_unmute = (prev, resp) => {
    const from_resp = resp && typeof resp === 'object' ? normalise_notify(resp.notify) : null;
    if (from_resp) return from_resp;
    return { ...(normalise_notify(prev) || { owner: false, following: false }), muted: false, mutedUntil: null };
};

const S = {
    quiet: { fontSize: 11.5, color: '#8a97a5', whiteSpace: 'nowrap', cursor: 'default' },
    btn: { fontSize: 12, padding: '2px 12px', borderRadius: 15, border: '1px solid #c9d4de', background: '#fff', color: '#3d4f5f', cursor: 'pointer', lineHeight: '18px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
    muted_row: { display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    failed: { fontSize: 11.5, color: '#c0392b' },
};

// on_change(notify): optional — lets the host patch its own copy of the
// channel row so a re-render from a later poll does not undo the click.
export default function NotifyToggle({ channel_id, notify, on_change, style }) {
    const known = normalise_notify(notify);
    const [state, set_state] = useState(known);
    const [busy, set_busy] = useState(false);
    const [unmute_failed, set_unmute_failed] = useState(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    // Re-sync when the host hands us a different channel or different values
    // (keyed on the primitives, not the object identity — feed polls rebuild
    // the row every time).
    const k_has = !!known, k_owner = !!(known && known.owner), k_following = !!(known && known.following), k_muted = !!(known && known.muted);
    const k_until = (known && known.mutedUntil) || null;
    useEffect(() => {
        set_state(k_has ? { owner: k_owner, following: k_following, muted: k_muted, mutedUntil: k_until } : null);
        set_unmute_failed(false);
    }, [channel_id, k_has, k_owner, k_following, k_muted, k_until]);

    const mode = notify_mode(state);
    if (!mode || channel_id == null || channel_id === '') return null;

    if (mode === 'owner') {
        return <span title={OWNER_TITLE} style={{ ...S.quiet, ...(style || {}) }}>{OWNER_LABEL}</span>;
    }
    if (mode === 'muted') {
        const unmute = async () => {
            if (busy) return;
            set_busy(true);
            set_unmute_failed(false);
            try {
                const r = await cs_analysis_fetch({ path: mute_path(channel_id), method: 'DELETE' });
                const next = after_unmute(state, r);
                if (alive.current) set_state(next);
                if (on_change) on_change(next);
            } catch (e) {
                // The representative reads a plain line; the server's words stay in the console.
                console.warn('NotifyToggle: unmute failed', (e && e.message) || e);
                if (alive.current) set_unmute_failed(true);
            }
            if (alive.current) set_busy(false);
        };
        return (
            <span style={{ ...S.muted_row, ...(style || {}) }}>
                <span title={MUTED_TITLE} style={S.quiet}>{muted_label(state)}</span>
                <button type='button' disabled={busy} onClick={unmute} title={UNMUTE_TITLE} style={S.btn}>
                    {UNMUTE_LABEL}
                </button>
                {unmute_failed ? <span role='alert' style={S.failed}>{UNMUTE_FAILED}</span> : null}
            </span>
        );
    }

    const following = mode === 'following';
    const toggle = async () => {
        if (busy) return;
        set_busy(true);
        try {
            const r = await cs_analysis_fetch({ path: follow_path(channel_id), method: following ? 'DELETE' : 'POST' });
            const next = next_notify(state, r, !following);
            if (alive.current) set_state(next);
            if (on_change) on_change(next);
        } catch (e) {
            window.alert((e && e.message) || 'could not update notifications');
        }
        if (alive.current) set_busy(false);
    };
    return (
        <button type='button' disabled={busy} onClick={toggle}
            title={following ? UNFOLLOW_TITLE : FOLLOW_TITLE}
            style={{ ...S.btn, border: '1px solid ' + (following ? '#2f6fed' : '#c9d4de'), color: following ? '#2f6fed' : '#3d4f5f', ...(style || {}) }}>
            <FaIcon name='star' regular={!following} style={{ marginRight: 4 }} />{following ? FOLLOWING_LABEL : FOLLOW_LABEL}
        </button>
    );
}
